/**
 * KAPE 2.0 · Instructor Assets Library
 *
 * Faculty upload their own photographs, diagrams, microscope images,
 * SOP images, branded graphics, logos, icons, illustrations, charts,
 * before/after examples, and approved classroom assets.
 *
 * Approved assets sit at the TOP of the image priority ladder — above
 * the University Media Library and above every AI image provider. That
 * means every future course reuses Dr. Christy's own materials first
 * and only falls back to Nano Banana / placeholder when nothing
 * matches.
 *
 * Route: /kcis/admin/kape/instructor-assets
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";

const ASSET_TYPES = [
  "photograph", "diagram", "microscope_image", "branded_graphic",
  "logo", "icon", "illustration", "sop_image", "chart",
  "before_after", "classroom_asset", "clinical_image", "anatomical",
  "process_graphic", "comparison",
];

const STATUS_FILTERS = ["all", "approved", "pending", "archived"];

export default function InstructorAssetsLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("");
  const [filterInstructor, setFilterInstructor] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterType)             params.set("asset_type", filterType);
      if (filterInstructor)       params.set("instructor_id", filterInstructor);
      if (q.trim())               params.set("q", q.trim());
      const { data } = await api.get(`/kcis/admin/kape/instructor-assets?${params}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load assets");
    } finally { setLoading(false); }
  }, [filterStatus, filterType, filterInstructor, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/kcis/admin/instructor-profiles");
        setProfiles((data.items || []).filter((p) => p.active !== false));
      } catch { /* ignore */ }
    })();
  }, []);

  const stats = useMemo(() => ({
    total: items.length,
    approved: items.filter((a) => a.status === "approved").length,
    pending:  items.filter((a) => a.status === "pending").length,
  }), [items]);

  return (
    <KcisAdminShell>
      <div className="p-6 space-y-6" data-testid="instructor-assets-page"
           style={{ background: PAL.bg, color: PAL.text, minHeight: "100vh" }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              KAPE 2.0 · Highest priority image source
            </div>
            <h1 className="text-2xl font-semibold">Instructor Assets Library</h1>
            <div className="text-sm mt-1" style={{ color: PAL.muted }}>
              {stats.total} asset(s) · {stats.approved} approved · {stats.pending} pending review
              · approved assets automatically replace AI-generated visuals on matching slides
            </div>
          </div>
          <button onClick={() => setUploadOpen(true)}
                  className="rounded-full px-4 py-2 text-sm font-medium"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid="ial-upload-btn">
            + Upload asset
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center"
             data-testid="ial-filters">
          <select value={filterInstructor} onChange={(e) => setFilterInstructor(e.target.value)}
                  className="rounded-md p-2 text-xs bg-transparent border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid="ial-filter-instructor">
            <option value="">All instructors</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p?.identity?.full_name || p.slug}</option>
            ))}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                  className="rounded-md p-2 text-xs bg-transparent border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid="ial-filter-type">
            <option value="">All asset types</option>
            {ASSET_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <div className="flex items-center gap-1">
            {STATUS_FILTERS.map((s) => (
              <button key={s} onClick={() => setFilterStatus(s)}
                      className={`text-xs rounded-full px-3 py-1 border ${filterStatus === s ? "font-semibold" : ""}`}
                      style={{ borderColor: PAL.border,
                                background: filterStatus === s ? PAL.accentSoft : "transparent",
                                color: filterStatus === s ? PAL.text : PAL.muted }}
                      data-testid={`ial-filter-status-${s}`}>
                {s}
              </button>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search title / keyword / topic"
                 className="rounded-md p-2 text-xs bg-transparent border flex-1 min-w-[220px]"
                 style={{ borderColor: PAL.border, color: PAL.text }}
                 data-testid="ial-search" />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-sm" style={{ color: PAL.muted }}>Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl p-6 text-sm border"
               style={{ background: PAL.panel, borderColor: PAL.border, color: PAL.muted }}
               data-testid="ial-empty">
            No assets match these filters. Click <b>Upload asset</b> to add your first photograph, diagram, or SOP image.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
               data-testid="ial-grid">
            {items.map((a) => (
              <AssetCard key={a.id} asset={a} onEdit={() => setEditing(a)}
                            onChanged={load} />
            ))}
          </div>
        )}

        {uploadOpen && (
          <UploadModal profiles={profiles}
                          onClose={() => setUploadOpen(false)}
                          onDone={() => { setUploadOpen(false); load(); }} />
        )}
        {editing && (
          <EditModal asset={editing}
                       onClose={() => setEditing(null)}
                       onDone={() => { setEditing(null); load(); }} />
        )}
      </div>
    </KcisAdminShell>
  );
}


function AssetCard({ asset, onEdit, onChanged }) {
  const statusStyle = {
    approved: { bg: "#065F46", fg: "#D1FAE5" },
    pending:  { bg: "#78350F", fg: "#FDE68A" },
    archived: { bg: "#374151", fg: "#D1D5DB" },
  }[asset.status] || { bg: "#374151", fg: "#D1D5DB" };

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const imgUrl = asset.url?.startsWith("http") ? asset.url : `${backend}${asset.url}`;

  const toggleApprove = async () => {
    try {
      if (asset.status === "approved") {
        await api.post(`/kcis/admin/kape/instructor-assets/${asset.id}/unapprove`);
        toast.success("Moved to pending");
      } else {
        await api.post(`/kcis/admin/kape/instructor-assets/${asset.id}/approve`);
        toast.success("Approved — will now be preferred over AI");
      }
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this asset? It will no longer be used for new generations.")) return;
    try {
      await api.delete(`/kcis/admin/kape/instructor-assets/${asset.id}`);
      toast.success("Archived");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const tags = asset.tags || {};
  return (
    <div className="rounded-xl overflow-hidden border" style={{ borderColor: PAL.border, background: PAL.panel }}
         data-testid={`ial-card-${asset.id}`}>
      <div className="aspect-square bg-black flex items-center justify-center overflow-hidden">
        <img src={imgUrl} alt={asset.alt_text || asset.title || ""}
             className="w-full h-full object-cover"
             data-testid={`ial-thumb-${asset.id}`} />
      </div>
      <div className="p-3 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ background: statusStyle.bg, color: statusStyle.fg }}
                data-testid={`ial-status-${asset.id}`}>{asset.status}</span>
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ background: PAL.accentSoft, color: PAL.text }}>
            {(asset.asset_type || "").replace(/_/g, " ")}
          </span>
        </div>
        <div className="text-sm font-medium truncate" title={asset.title}>{asset.title || "Untitled"}</div>
        <div className="text-[11px]" style={{ color: PAL.muted }}>
          {[tags.college, tags.course_title, tags.lesson_title].filter(Boolean).join(" · ") || "No course tagged"}
        </div>
        {tags.keywords?.length ? (
          <div className="text-[10px] truncate" style={{ color: PAL.muted }} title={tags.keywords.join(", ")}>
            #{tags.keywords.slice(0, 4).join("  #")}
          </div>
        ) : null}
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <button onClick={toggleApprove}
                  className="text-[11px] rounded-full px-3 py-1 font-semibold"
                  style={asset.status === "approved"
                    ? { background: "#374151", color: "#F3F4F6" }
                    : { background: "#10B981", color: "white" }}
                  data-testid={`ial-approve-${asset.id}`}>
            {asset.status === "approved" ? "Unapprove" : "Approve"}
          </button>
          <button onClick={onEdit}
                  className="text-[11px] rounded-full px-3 py-1 border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid={`ial-edit-${asset.id}`}>Edit</button>
          {asset.status !== "archived" && (
            <button onClick={archive}
                    className="text-[11px] rounded-full px-3 py-1 border ml-auto"
                    style={{ borderColor: PAL.border, color: "#F87171" }}
                    data-testid={`ial-archive-${asset.id}`}>Archive</button>
          )}
        </div>
      </div>
    </div>
  );
}


function UploadModal({ profiles, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    instructor_id: profiles?.[0]?.id || "",
    title: "", asset_type: "photograph", visual_type: "",
    alt_text: "", caption: "", license: "instructor-owned",
    tags: { college: "", program: "", course_id: "", module_id: "",
             lesson_id: "", course_title: "", module_title: "",
             lesson_title: "", topics: "", keywords: "" },
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setTag = (k, v) => setForm((s) => ({ ...s, tags: { ...s.tags, [k]: v } }));

  const submit = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    if (!form.instructor_id) { toast.error("Instructor is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("instructor_id", form.instructor_id);
      fd.append("title", form.title);
      fd.append("asset_type", form.asset_type);
      fd.append("visual_type", form.visual_type);
      fd.append("alt_text", form.alt_text);
      fd.append("caption", form.caption);
      fd.append("license", form.license);
      // topics / keywords: comma-separated → array server-side normalises
      fd.append("tags_json", JSON.stringify({
        ...form.tags,
        topics: form.tags.topics,
        keywords: form.tags.keywords,
      }));
      await api.post("/kcis/admin/kape/instructor-assets", fd,
                       { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — approve it to make it a preferred visual");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Upload instructor asset" onClose={onClose}
                 testId="ial-upload-modal"
                 footer={(
                   <button onClick={submit} disabled={busy || !file}
                             className="text-xs rounded-full px-4 py-2 font-semibold disabled:opacity-40"
                             style={{ background: PAL.accent, color: "#0F0B14" }}
                             data-testid="ial-upload-submit">
                     {busy ? "Uploading…" : "Upload asset"}
                   </button>
                 )}>
      <input type="file" accept="image/*"
             onChange={(e) => setFile(e.target.files?.[0] || null)}
             className="text-xs" data-testid="ial-upload-file" />
      <TextRow label="Instructor" required>
        <select value={form.instructor_id}
                onChange={(e) => set("instructor_id", e.target.value)}
                className="w-full rounded p-2 text-xs bg-transparent border"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid="ial-upload-instructor">
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>{p?.identity?.full_name || p.slug}</option>
          ))}
        </select>
      </TextRow>
      <TextRow label="Title">
        <input value={form.title} onChange={(e) => set("title", e.target.value)}
               className="w-full rounded p-2 text-xs bg-transparent border"
               style={{ borderColor: PAL.border, color: PAL.text }}
               data-testid="ial-upload-title" />
      </TextRow>
      <TextRow label="Asset type">
        <select value={form.asset_type} onChange={(e) => set("asset_type", e.target.value)}
                className="w-full rounded p-2 text-xs bg-transparent border"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid="ial-upload-type">
          {ASSET_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
      </TextRow>
      <TextRow label="Alt text (accessibility)">
        <input value={form.alt_text} onChange={(e) => set("alt_text", e.target.value)}
               className="w-full rounded p-2 text-xs bg-transparent border"
               style={{ borderColor: PAL.border, color: PAL.text }}
               data-testid="ial-upload-alt" />
      </TextRow>
      <TextRow label="Caption">
        <input value={form.caption} onChange={(e) => set("caption", e.target.value)}
               className="w-full rounded p-2 text-xs bg-transparent border"
               style={{ borderColor: PAL.border, color: PAL.text }}
               data-testid="ial-upload-caption" />
      </TextRow>
      <TagFields tags={form.tags} setTag={setTag} />
    </ModalShell>
  );
}


function EditModal({ asset, onClose, onDone }) {
  const [form, setForm] = useState({
    title: asset.title || "", asset_type: asset.asset_type || "photograph",
    alt_text: asset.alt_text || "", caption: asset.caption || "",
    license: asset.license || "instructor-owned",
    tags: {
      college: asset.tags?.college || "", program: asset.tags?.program || "",
      course_id: asset.tags?.course_id || "", module_id: asset.tags?.module_id || "",
      lesson_id: asset.tags?.lesson_id || "", course_title: asset.tags?.course_title || "",
      module_title: asset.tags?.module_title || "", lesson_title: asset.tags?.lesson_title || "",
      topics: (asset.tags?.topics || []).join(", "),
      keywords: (asset.tags?.keywords || []).join(", "),
    },
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setTag = (k, v) => setForm((s) => ({ ...s, tags: { ...s.tags, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/kcis/admin/kape/instructor-assets/${asset.id}`, {
        title: form.title, asset_type: form.asset_type,
        alt_text: form.alt_text, caption: form.caption, license: form.license,
        tags: form.tags,
      });
      toast.success("Saved");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Edit · ${asset.title || asset.id.slice(0, 8)}`}
                 onClose={onClose} testId="ial-edit-modal"
                 footer={(
                   <button onClick={save} disabled={busy}
                             className="text-xs rounded-full px-4 py-2 font-semibold disabled:opacity-40"
                             style={{ background: PAL.accent, color: "#0F0B14" }}
                             data-testid="ial-edit-submit">
                     {busy ? "Saving…" : "Save"}
                   </button>
                 )}>
      <TextRow label="Title">
        <input value={form.title} onChange={(e) => set("title", e.target.value)}
               className="w-full rounded p-2 text-xs bg-transparent border"
               style={{ borderColor: PAL.border, color: PAL.text }}
               data-testid="ial-edit-title" />
      </TextRow>
      <TextRow label="Asset type">
        <select value={form.asset_type} onChange={(e) => set("asset_type", e.target.value)}
                className="w-full rounded p-2 text-xs bg-transparent border"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid="ial-edit-type">
          {ASSET_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
      </TextRow>
      <TextRow label="Alt text">
        <input value={form.alt_text} onChange={(e) => set("alt_text", e.target.value)}
               className="w-full rounded p-2 text-xs bg-transparent border"
               style={{ borderColor: PAL.border, color: PAL.text }} />
      </TextRow>
      <TextRow label="Caption">
        <input value={form.caption} onChange={(e) => set("caption", e.target.value)}
               className="w-full rounded p-2 text-xs bg-transparent border"
               style={{ borderColor: PAL.border, color: PAL.text }} />
      </TextRow>
      <TagFields tags={form.tags} setTag={setTag} />
    </ModalShell>
  );
}


function TagFields({ tags, setTag }) {
  return (
    <div className="mt-3 rounded-lg p-3"
         style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
         data-testid="ial-tags-block">
      <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: PAL.muted }}>
        Tags — where this asset applies
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <TextRow label="College">
          <input value={tags.college} onChange={(e) => setTag("college", e.target.value)}
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }}
                 data-testid="ial-tag-college" />
        </TextRow>
        <TextRow label="Program">
          <input value={tags.program} onChange={(e) => setTag("program", e.target.value)}
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }} />
        </TextRow>
        <TextRow label="Course ID">
          <input value={tags.course_id} onChange={(e) => setTag("course_id", e.target.value)}
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }}
                 data-testid="ial-tag-course-id" />
        </TextRow>
        <TextRow label="Course title">
          <input value={tags.course_title} onChange={(e) => setTag("course_title", e.target.value)}
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }} />
        </TextRow>
        <TextRow label="Module ID">
          <input value={tags.module_id} onChange={(e) => setTag("module_id", e.target.value)}
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }} />
        </TextRow>
        <TextRow label="Lesson ID">
          <input value={tags.lesson_id} onChange={(e) => setTag("lesson_id", e.target.value)}
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }} />
        </TextRow>
        <TextRow label="Topics (comma separated)">
          <input value={tags.topics} onChange={(e) => setTag("topics", e.target.value)}
                 placeholder="microscopy, follicle, scalp"
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }}
                 data-testid="ial-tag-topics" />
        </TextRow>
        <TextRow label="Keywords (comma separated)">
          <input value={tags.keywords} onChange={(e) => setTag("keywords", e.target.value)}
                 placeholder="cuticle, cortex, medulla"
                 className="w-full rounded p-2 text-xs bg-transparent border"
                 style={{ borderColor: PAL.border, color: PAL.text }}
                 data-testid="ial-tag-keywords" />
        </TextRow>
      </div>
    </div>
  );
}


function TextRow({ label, children, required }) {
  return (
    <div className="mt-2">
      <label className="text-[10px] uppercase tracking-widest" style={{ color: PAL.muted }}>
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


function ModalShell({ title, children, footer, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
         role="dialog" data-testid={testId}
         onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl"
           style={{ background: PAL.panel, border: `1px solid ${PAL.border}`,
                     color: PAL.text }}>
        <div className="px-5 py-3 border-b flex items-center justify-between"
             style={{ borderColor: PAL.border }}>
          <div className="text-sm font-semibold">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-lg"
                  aria-label="Close" data-testid={`${testId}-close`}>×</button>
        </div>
        <div className="p-5">{children}</div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2"
             style={{ borderColor: PAL.border }}>{footer}</div>
      </div>
    </div>
  );
}
